import capitalize from "lodash/capitalize"

import { present } from "./utils/presence"

// Inputs to the format-locale composition. Language and region resolve
// independently, then compose into a BCP-47 `language-region` string. Browser
// signals only participate post-mount (so server and first client render agree),
// so callers omit them on the server.
export interface ComposeLocaleInputs {
  userLanguage?: string | null // the user's saved language preference
  userRegion?: string | null // the user's saved region preference
  embeddedLanguage?: string | null // embedded host forces the language
  embeddedRegion?: string | null // embedded host forces the region
  browserLanguage?: string | null // device language (post-mount only)
  browserRegion?: string | null // device region (post-mount only)
}

// Compose a BCP-47 `language-region` format locale, each subtag independently:
// embedded → user → browser, defaulting to "en-US". The embedded host outranks the
// saved preference because the widget follows the partner page it's on — and the
// preference may itself be a snapshot of an earlier host locale, not a choice.
// Unreleased languages resolve as if unset on the web surface — but never for
// the embedded slot, since embedded orgs still serve them. Region is independent,
// so an unreleased-language user keeps their regional formats.
// Pure and React-free; the React seam lives in useLocale() / selectLocale.
export function composeLocale({
  userLanguage,
  userRegion,
  embeddedLanguage,
  embeddedRegion,
  browserLanguage,
  browserRegion,
}: ComposeLocaleInputs): string {
  const language =
    [
      embeddedLanguage,
      unlessUnreleasedLanguage(userLanguage),
      unlessUnreleasedLanguage(browserLanguage),
    ].find(present) ?? "en"
  // User Portuguese defaults to Brazil pt-BR, if not explicit pt in user or embedded context
  const userIsPt = !embeddedLanguage && !embeddedRegion && language === "pt"
  const region =
    ([embeddedRegion, userRegion].find(present) ??
      (userIsPt ? "BR" : browserRegion)) ||
    "US"
  return new Intl.Locale(language, { region }).baseName
}

// A language (or full locale tag) with an unreleased language subtag resolves as
// undefined, so composition falls through to the next source / English.
function unlessUnreleasedLanguage(
  locale: string | null | undefined
): string | null | undefined {
  const language = parseLocale(locale ?? undefined).language
  return language && UNRELEASED_LOCALE_CODES.includes(language)
    ? undefined
    : locale
}

export function getBrowserLanguageRegion(): {
  browserLanguage?: string
  browserRegion?: string
} {
  try {
    const resolved = new Intl.Locale(
      Intl.DateTimeFormat().resolvedOptions().locale
    )
    return {
      browserLanguage: resolved.language,
      browserRegion: resolved.region,
    }
  } catch {
    return {}
  }
}

// Parse a BCP-47 locale string (e.g. "fr-FR", "pt", "zh-Hant-TW") into its
// language and region subtags via Intl.Locale — never string-split. Either subtag
// may be undefined if the input omits it. Returns {} on garbage.
export function parseLocale(locale: string | undefined): {
  language?: string
  region?: string
} {
  if (!locale?.trim()) return {}
  try {
    const resolved = new Intl.Locale(locale)
    return { language: resolved.language, region: resolved.region }
  } catch {
    return {}
  }
}

// Maps a supported language subtag to the code we want to DISPLAY it under.
// We support Norwegian Bokmål ("nb") but users think of it as plain "Norwegian",
const DISPLAY_LANGUAGE_ALIASES: Record<string, string> = { nb: "no" }

function toDisplayLanguage(language: string): string {
  return DISPLAY_LANGUAGE_ALIASES[language] ?? language
}

// Names we override Intl for, to distinguish European (pt) from Brazilian (pt-BR)
// Portuguese, and Canadian French (fr-CA). Keyed by full tag, then bare language.
const LANGUAGE_NAME_OVERRIDES: Record<string, string> = {
  pt: "Portuguese (Europe)",
  "pt-BR": "Portuguese (Brazil)",
  "fr-CA": "French (Canada)",
}

// English display name of a locale's LANGUAGE (e.g. "fr-FR" -> "French"). Resolves the
// language subtag so region-qualified locales don't widen to "French (France)", except
// the LANGUAGE_NAME_OVERRIDES above. Empty string for English and for garbage.
export function getLanguageName(locale: string | undefined): string {
  const { language, region } = parseLocale(locale)
  if (!language || language.startsWith("en")) return ""
  const tag = region ? `${language}-${region}` : language
  return (
    LANGUAGE_NAME_OVERRIDES[tag] ??
    LANGUAGE_NAME_OVERRIDES[language] ??
    capitalize(
      new Intl.DisplayNames(["en"], { type: "language" }).of(
        toDisplayLanguage(language)
      )
    )
  )
}

// Localized display name of a region subtag (e.g. "US" -> "United States", "GB" ->
// "United Kingdom"). Names render in `displayLocale` (the resolved format locale, so
// e.g. a French user sees "États-Unis"), falling back to "en". Empty string for an
// unknown or garbage code.
export function getRegionName(region: string, displayLocale = "en"): string {
  try {
    return (
      new Intl.DisplayNames([displayLocale, "en"], { type: "region" }).of(
        region
      ) ?? ""
    )
  } catch {
    return ""
  }
}

// Flag emoji for an ISO 3166-1 alpha-2 region code (e.g. "US" -> 🇺🇸). Empty string
// for invalid codes.
export function getRegionFlag(region: string): string {
  const code = region.toUpperCase()
  if (!/^[A-Z]{2}$/.test(code)) return ""
  return String.fromCodePoint(
    ...[...code].map((char) => 0x1f1e6 - 65 + char.charCodeAt(0))
  )
}

// Curated region subtags offered by the settings region picker. Region drives
// date/number/currency formatting, so this spans the major markets we care to test
// against rather than every ISO 3166 code. No backend list to mirror yet (the saved
// preference is client-only for now).
export const SUPPORTED_REGION_CODES = [
  "US",
  "GB",
  "CA",
  "AU",
  "IE",
  "FR",
  "BE",
  "CH",
  "DE",
  "AT",
  "ES",
  "MX",
  "IT",
  "NL",
  "PT",
  "BR",
  "NO",
  "SE",
  "DK",
  "CN",
  "TW",
  "JP",
  "KR",
  "IN",
  "SG",
] as const satisfies readonly string[]

// Union of the region subtags above (e.g. "US" | "GB" | ...). Used to make tables
// like REGION_CURRENCY prove at compile time that every supported region is covered.
export type SupportedRegionCode = (typeof SUPPORTED_REGION_CODES)[number]

// utilities for language selection and formatting
export interface Locale {
  code: string
  name: string
}

// Languages released on the consumer web surface: offered in the settings picker
// and honored by auto-detection. Same split defined in backend/lib/locale.rb.
export const RELEASED_LOCALE_CODES: string[] = [
  "es",
  "fr",
  "de",
  "it",
  "pt",
  "nl",
  "nb",
]

// Supported but not yet released on the consumer web surface, until entity names
// survive translation in these languages (CJK transliterates place names); embedded
// orgs still serve them. Releasing one = moving it to RELEASED_LOCALE_CODES here
// and in backend/lib/locale.rb.
export const UNRELEASED_LOCALE_CODES: string[] = ["zh", "ja", "ko"]

// Everything the product can translate into (embedded orgs + curated catalogs).
export const SUPPORTED_LOCALE_CODES: string[] = [
  ...RELEASED_LOCALE_CODES,
  ...UNRELEASED_LOCALE_CODES,
]

export const SUPPORTED_LOCALES: Locale[] = SUPPORTED_LOCALE_CODES.map(
  (code) => ({
    code,
    name: getLanguageName(code),
  })
)

// Locales an embedded org can pick for translation: the consumer languages plus the
// region variants (pt-BR, fr-CA) orgs serve to specific audiences. The consumer
// settings picker keeps using SUPPORTED_LOCALES (languages only).
export const ORG_TRANSLATION_LOCALE_CODES: string[] = [
  ...SUPPORTED_LOCALE_CODES,
  "pt-BR",
  "fr-CA",
]

export const ORG_TRANSLATION_LOCALES: Locale[] =
  ORG_TRANSLATION_LOCALE_CODES.map((code) => ({
    code,
    name: getLanguageName(code),
  })).sort((a, b) => a.code.localeCompare(b.code))

// parse comma-separated language codes into Language objects
export function parseLocaleCodes(codes: string | undefined): Locale[] {
  if (!codes?.trim()) return []
  return codes
    .split(",")
    .map((code) => code.trim())
    .filter((code) => code && getLanguageName(code))
    .map((code) => ({ code, name: getLanguageName(code) }))
}

// convert locale objects back to comma-separated codes string
export function formatLocaleCodes(locales: Locale[]): string {
  return locales.map((lang) => lang.code).join(",")
}

// get available languages from `pool` (default: the consumer set) not already selected
export function getAvailableLocales(
  selected: Locale[],
  pool: Locale[] = SUPPORTED_LOCALES
): Locale[] {
  const selectedCodes = new Set(selected.map((lang) => lang.code))
  return pool.filter((lang) => !selectedCodes.has(lang.code))
}

// A language's autonym: its name written in that language itself (e.g. "fr" ->
// "Français", "ja" -> "日本語"). Locale-INVARIANT — an autonym doesn't depend on
// the UI locale (subject and display locale are the same code) — so this is a
// plain util, NOT a Locale hook (cf. currencySymbol). Use it for language
// pickers, where each option should read in its own language.
export function languageAutonym(languageCode: string): string {
  const displayCode = toDisplayLanguage(languageCode)
  try {
    const name = new Intl.DisplayNames([displayCode], { type: "language" }).of(
      displayCode
    )
    return name ?? languageCode
  } catch {
    return languageCode
  }
}

// The index of the first day of the week, Sunday=0 … Saturday=6 — the scale
// used by react-day-picker's `weekStartsOn` prop.
export type WeekStart = 0 | 1 | 2 | 3 | 4 | 5 | 6

// Intl.Locale.prototype.getWeekInfo() is not yet in TypeScript's lib types, so
// we describe just the slice we read. firstDay is ISO (1=Monday … 7=Sunday).
interface LocaleWithWeekInfo {
  getWeekInfo(): { firstDay: number }
}

function supportsWeekInfo(
  locale: Intl.Locale
): locale is Intl.Locale & LocaleWithWeekInfo {
  return "getWeekInfo" in locale && typeof locale.getWeekInfo === "function"
}

// ISO firstDay (1=Monday … 7=Sunday) → react-day-picker's scale (Sunday=0).
function firstDayToWeekStart(firstDay: number): WeekStart {
  switch (firstDay) {
    case 1:
      return 1
    case 2:
      return 2
    case 3:
      return 3
    case 4:
      return 4
    case 5:
      return 5
    case 6:
      return 6
    default:
      return 0 // 7 (Sunday) and any out-of-range value
  }
}

// Resolve the locale's first day of the week. Falls back to Sunday (0) when the
// runtime lacks getWeekInfo() (older browsers) or the locale can't be parsed.
export const getWeekStartsOn = (locale: string): WeekStart => {
  try {
    const intlLocale = new Intl.Locale(locale)
    if (!supportsWeekInfo(intlLocale)) return 0
    return firstDayToWeekStart(intlLocale.getWeekInfo().firstDay)
  } catch {
    return 0
  }
}
